import { Trans, t } from "@web-monorepo/i18n/macro";
import { DDSButton, DDSHeadlineText, DDSIconButton, DDSTextField } from "@web-monorepo/dds";
import { IconPlus } from "@web-monorepo/dds-icons";
import { uniqueId } from "es-toolkit/compat";
import PersonListItem, { Person } from "components/PersonListItem";
import { useCallback, useMemo, useState } from "react";
import { logParentToolkitEvent } from "src/utils/logEvent";
import styles from "./EditPeopleModal.module.css";

type EditPeopleModalProps = {
  onClose: VoidFunction;
  onComplete: (additionalPersons: Person[], deselectedIds: string[]) => void;
  persons: Person[];
  initialDeselectedIds: string[];
};

export default function EditPeopleModal({ onClose, onComplete, persons, initialDeselectedIds }: EditPeopleModalProps) {
  const [pendingAdditionalPersons, setPendingAdditionalPersons] = useState<Person[]>([]);
  const [pendingDeselectedIds, setPendingDeselectedIds] = useState(initialDeselectedIds);
  const [newPerson, setNewPerson] = useState("");
  const addPersonLabel = t`Add person`;

  const selectablePersons = useMemo(
    () => persons.concat(pendingAdditionalPersons),
    [persons, pendingAdditionalPersons],
  );

  const trimmedNewPerson = useMemo(() => newPerson.trim(), [newPerson]);

  const addPerson = useCallback(() => {
    if (!trimmedNewPerson) {
      setNewPerson("");
      return;
    }
    logParentToolkitEvent({ eventName: `whose_turn.edit.add.tap` });
    setPendingAdditionalPersons([
      ...pendingAdditionalPersons,
      {
        _id: uniqueId(trimmedNewPerson),
        firstName: trimmedNewPerson,
        avatar: `//pstatic.classdojo.com/img2/monsters_v5/128/cute_v5_${Math.floor(Math.random() * 20) + 1}.png`,
      },
    ]);
    setNewPerson("");
  }, [setPendingAdditionalPersons, pendingAdditionalPersons, trimmedNewPerson, setNewPerson]);

  const togglePerson = useCallback(
    (id: string) => {
      if (pendingDeselectedIds.includes(id)) {
        setPendingDeselectedIds(pendingDeselectedIds.filter((i) => i !== id));
      } else {
        setPendingDeselectedIds([...pendingDeselectedIds, id]);
      }
    },
    [pendingDeselectedIds, setPendingDeselectedIds],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerSideLeft}>
          <DDSButton data-name="edit_people_modal:toolkit.whose_turn.cancel" variant="secondary" onClick={onClose}>
            <Trans>Cancel</Trans>
          </DDSButton>
        </div>
        <DDSHeadlineText as="h2" level="md">
          <Trans>People</Trans>
        </DDSHeadlineText>
        <div className={styles.headerSideRight}>
          <DDSButton
            data-name="edit_people_modal:toolkit.whose_turn.done"
            variant="secondary"
            onClick={() => onComplete(pendingAdditionalPersons, pendingDeselectedIds)}
          >
            <Trans>Done</Trans>
          </DDSButton>
        </div>
      </div>
      <div className={styles.personList}>
        {selectablePersons.map((person) => {
          return (
            <PersonListItem
              key={person._id}
              person={person}
              selected={!pendingDeselectedIds.includes(person._id)}
              toggle={togglePerson}
            />
          );
        })}
      </div>
      <div className={styles.addRow}>
        <div className={styles.addInput}>
          <DDSTextField
            data-name="edit_people_modal:add_person_input"
            label={addPersonLabel}
            placeholder={addPersonLabel}
            onChange={setNewPerson}
            value={newPerson}
          />
        </div>
        <DDSIconButton
          data-name="edit_people_modal:add_button"
          aria-label={addPersonLabel}
          tooltip={{ content: addPersonLabel, dataName: "edit_people_modal:add_button:tooltip" }}
          variant="primary"
          onClick={addPerson}
        >
          <IconPlus />
        </DDSIconButton>
      </div>
    </div>
  );
}
